import { difference, union } from 'lodash-es';

import {
	checkElementsDuplicates,
	getIncomingParent
} from '@editor2/services/conflictResolutionService';
import { type Conflict, type ReorderConflict } from '@editor2/types/conflict';
import { type CourseState } from '@editor2/types/courseState';
import { getPageByElementId } from '@editor2/components/CourseChangesDiff/helpers';
import { ChangesStatus } from '@editor2/types/changesStatus';
import {
	handleAcceptCurrentElementsReorder,
	handleNewDeletedInCurrentElementsReorder
} from '../common/conflictResolutionHelper';

export class PageChildrenReorderConflictResolver {
	acceptCurrent = (state: CourseState, conflict: Conflict | ReorderConflict) => {
		const { entityId, attribute, initial, current, incoming } = conflict as ReorderConflict;
		const deletedOrMovedToInIncoming = difference(initial, incoming);
		const adedOrMovedFromInIncoming = difference(incoming, current);

		state.initialCourse.pages.entities[entityId].elements = incoming;
		state.pages.entities[entityId].elements = current;

		handleAcceptCurrentElementsReorder({
			state,
			attribute,
			deletedOrMovedToInIncoming,
			adedOrMovedFromInIncoming
		});

		checkElementsDuplicates(state, entityId);
	};

	acceptIncoming = (state: CourseState, conflict: Conflict | ReorderConflict) => {
		// 1 - discard current changes
		// 2 - apply incoming changes
		const { entityId, initial, current, incoming } = conflict as ReorderConflict;
		const deletedOrMovedToInCurrent = difference(initial, current);
		const addedOrMovedFromInCurrent = difference(current, initial);

		handleNewDeletedInCurrentElementsReorder({
			state,
			deletedOrMovedToInCurrent,
			addedOrMovedFromInCurrent
		});

		state.initialCourse.pages.entities[entityId].elements = incoming;
		state.pages.entities[entityId].elements = incoming;

		checkElementsDuplicates(state, entityId);
	};

	acceptBoth = (state: CourseState, conflict: Conflict | ReorderConflict) => {
		const { entityId, attribute, initial, current, incoming } = conflict as ReorderConflict;
		const deletedCurrent = difference(initial, current);
		const deletedIncoming = difference(initial, incoming);
		const merged = union(current, incoming);

		deletedCurrent.map((elementId) => {
			const currentParent = getPageByElementId(state.pages, elementId);
			if (!currentParent) {
				state.elements[elementId] = state.initialCourse.elements.entities[elementId];
				state.changes.elements[elementId] = [];
			}
		});
		deletedIncoming.map((elementId) => {
			const incomingParent = getIncomingParent({ attribute, id: elementId, state });
			if (!incomingParent) {
				state.changes.elements[elementId] = [ChangesStatus.New];
			}
		});

		state.initialCourse.pages.entities[entityId].elements = incoming;
		state.pages.entities[entityId].elements = merged;

		checkElementsDuplicates(state, entityId);
	};
}
